import {
  BrainCog,
  CircleDollarSign,
  Folders,
  GitFork,
  KeyRound,
  PencilRuler,
  Plus,
  Plug,
  Sparkle,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import { usePermissions } from "@/hooks/usePermissions";

export type NavItem = {
  key: string;
  label: string;
  to: string;
  icon: LucideIcon;
  activeMatch: "exact" | "path-prefix";
  visible: boolean;
};

export type NavSection = {
  key: string;
  /** Sections without a label render as a bare list, per the design. */
  label?: string;
  collapsible: boolean;
  items: NavItem[];
};

export const isNavItemActive = (pathname: string, item: NavItem) => {
  const normalizedPathname = pathname.toLowerCase();
  const normalizedTarget = item.to.toLowerCase();

  if (normalizedPathname === normalizedTarget) return true;
  if (item.key === "deployments" && normalizedPathname === "/predefined-models") return true;

  return (
    item.activeMatch === "path-prefix" &&
    normalizedPathname.startsWith(`${normalizedTarget}/`)
  );
};

/**
 * The navigation contract, carried over from the old DashboardLayout.
 *
 * The visibility rules are the part worth preserving verbatim — they are the only
 * thing standing between an inference-only org and pages it must not see. The
 * grouping and labels are new, from the redesign.
 *
 * Note `/mcps` and `/blueprints` in lowercase. The old layout linked to "/MCPS",
 * and the legacy tree declared Blueprints as "/bluePrints" while linking to
 * "/blueprints". Both worked because react-router v6 matches case-insensitively —
 * and both still work, because TanStack Router does too (`caseSensitive` defaults
 * to false; seam.test.ts pins it). Canonical lowercase here is hygiene, not a fix
 * for a breakage: one spelling per screen, so the active-state check has something
 * stable to compare against.
 */
export const useNavSections = (): NavSection[] => {
  const { t } = useTranslation();
  const { canViewDeployments, canViewApiKey, isInferenceOnly } =
    usePermissions();

  const sections: NavSection[] = [
    {
      key: "primary",
      collapsible: false,
      items: [
        {
          key: "newChat",
          label: t("sidebar.newChat"),
          to: "/playground",
          icon: Plus,
          activeMatch: "exact",
          visible: true,
        },
        {
          key: "blueprints",
          label: t("sidebar.bluePrints"),
          to: "/blueprints",
          icon: PencilRuler,
          activeMatch: "exact",
          visible: !isInferenceOnly,
        },
        {
          key: "apiKeys",
          label: t("sidebar.apiKeys"),
          to: "/platform/security/api-keys",
          icon: KeyRound,
          activeMatch: "exact",
          visible: canViewApiKey,
        },
        {
          key: "usageCost",
          label: t("sidebar.usageCost"),
          to: "/usage",
          icon: CircleDollarSign,
          activeMatch: "exact",
          visible: true,
        },
      ],
    },
    {
      key: "knowledgeBase",
      label: t("sidebar.knowledgeBase"),
      collapsible: true,
      items: [
        {
          key: "collections",
          label: t("sidebar.documentCollections"),
          to: "/collections",
          icon: Folders,
          activeMatch: "path-prefix",
          visible: !isInferenceOnly,
        },
        {
          key: "connectors",
          label: t("sidebar.connectors"),
          to: "/mcps",
          icon: Plug,
          activeMatch: "exact",
          visible: !isInferenceOnly,
        },
        {
          key: "flows",
          label: t("sidebar.flows"),
          to: "/flows",
          icon: GitFork,
          activeMatch: "path-prefix",
          visible: !isInferenceOnly,
        },
        {
          key: "aiCatalog",
          label: t("sidebar.aiCatalog"),
          to: "/ai-catalog",
          icon: Sparkle,
          activeMatch: "exact",
          visible: true,
        },
        {
          key: "deployments",
          label: t("sidebar.deployments"),
          to: "/deployments",
          icon: BrainCog,
          // Omnimizer subsections live under /deployments/*.
          activeMatch: "path-prefix",
          visible: canViewDeployments,
        },
      ],
    },
  ];

  return sections
    .map((s) => ({ ...s, items: s.items.filter((i) => i.visible) }))
    .filter((s) => s.items.length > 0);
};
